import { useEffect, useMemo, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { ApiError, iptv, type IptvChannel } from '../api';
import { ChannelLogo, ChannelName, channelDisplay } from '../components/LiveBits';
import { goBack } from '../router';

/**
 * Multiview: tot 4 zenders tegelijk (2x2). Geluid volgt de gekozen tegel.
 * Elke tegel telt als een eigen stream bij de provider (verbindingslimiet).
 */

type Slot = { accountId: string; streamId: number } | null;
const KEY = 'tumedia.multiview';
const SLOTS = 4;

function loadSlots(): Slot[] {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) ?? '[]') as Slot[];
    return Array.from({ length: SLOTS }, (_, i) => s[i] ?? null);
  } catch {
    return Array.from({ length: SLOTS }, () => null);
  }
}

function saveSlots(s: Slot[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // niet erg: dan alleen voor deze sessie
  }
}

/** Zender toevoegen aan de eerste lege tegel (vanuit de live-player). */
export function addToMultiview(accountId: string, streamId: number): void {
  const s = loadSlots();
  if (s.some((x) => x?.accountId === accountId && x.streamId === streamId)) return;
  const i = s.findIndex((x) => x === null);
  s[i === -1 ? SLOTS - 1 : i] = { accountId, streamId };
  saveSlots(s);
}

export function Multiview() {
  const [slots, setSlots] = useState<Slot[]>(loadSlots);
  const [channels, setChannels] = useState<IptvChannel[]>([]);
  const [active, setActive] = useState(0);
  const [picking, setPicking] = useState<number | null>(null);

  useEffect(() => {
    iptv.channels().then((r) => setChannels(r.channels)).catch(() => setChannels([]));
  }, []);

  useEffect(() => saveSlots(slots), [slots]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || (e.key === 'Backspace' && (e.target as HTMLElement)?.tagName !== 'INPUT')) {
        e.preventDefault();
        e.stopPropagation();
        if (picking !== null) setPicking(null);
        else goBack();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [picking]);

  const setSlot = (i: number, v: Slot) => setSlots((s) => s.map((x, j) => (j === i ? v : x)));
  const byKey = useMemo(() => new Map(channels.map((c) => [`${c.accountId}:${c.streamId}`, c])), [channels]);

  return (
    <div className="multiview">
      <header className="mv-head">
        <button className="icon-btn" onClick={goBack} aria-label="Terug">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <strong>Multiview</strong>
        <span className="subtle">Klik op een tegel voor het geluid. Elke tegel gebruikt een eigen stream.</span>
      </header>
      <div className="mv-grid">
        {slots.map((slot, i) => {
          const ch = slot ? byKey.get(`${slot.accountId}:${slot.streamId}`) : undefined;
          return (
            <section key={i} className={`mv-tile ${active === i ? 'is-active' : ''}`} aria-label={`Tegel ${i + 1}${ch ? `: ${ch.name}` : ''}`}>
              {slot ? (
                <>
                  <MiniPlayer key={`${slot.accountId}:${slot.streamId}`} slot={`mv${i + 1}`} accountId={slot.accountId} streamId={slot.streamId} muted={active !== i} onActivate={() => setActive(i)} />
                  <div className="mv-bar">
                    {ch && <ChannelLogo src={ch.logo} name={ch.name} size={24} />}
                    <span className="mv-name">{ch ? channelDisplay(ch.name).name : 'Zender'}</span>
                    {active === i && <span className="mv-audio" aria-label="Geluid aan">Geluid</span>}
                    <button className="chip" onClick={() => setPicking(i)}>Wissel</button>
                    <button className="chip" onClick={() => setSlot(i, null)} aria-label="Tegel leegmaken">✕</button>
                  </div>
                </>
              ) : (
                <button className="mv-empty" onClick={() => setPicking(i)} data-autofocus={i === slots.findIndex((s) => s === null) ? '' : undefined}>
                  + Zender kiezen
                </button>
              )}
            </section>
          );
        })}
      </div>
      {picking !== null && (
        <ChannelPicker
          channels={channels}
          onPick={(c) => {
            setSlot(picking, { accountId: c.accountId, streamId: c.streamId });
            setActive(picking);
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}

function MiniPlayer({ accountId, streamId, slot, muted, onActivate }: { accountId: string; streamId: number; slot: string; muted: boolean; onActivate: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    let cancelled = false;
    let sid: string | null = null;
    let hls: Hls | null = null;
    iptv.play(accountId, streamId, { slot, fmp4: true })
      .then(async ({ sessionId, url }) => {
        sid = sessionId;
        if (cancelled) return;
        if (el.canPlayType('application/vnd.apple.mpegurl')) {
          el.src = url;
        } else {
          const { default: HlsLib } = await import('hls.js');
          if (cancelled) return;
          hls = new HlsLib({ liveSyncDurationCount: 3, maxBufferLength: 20, capLevelToPlayerSize: true });
          hls.on(HlsLib.Events.ERROR, (_e, d) => {
            if (d.fatal) setError(d.type === HlsLib.ErrorTypes.MEDIA_ERROR ? 'Beeldformaat niet ondersteund' : 'Zender weggevallen');
          });
          hls.loadSource(url);
          hls.attachMedia(el);
        }
        el.muted = true;
        el.play().catch(() => undefined);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Zender kon niet starten');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      hls?.destroy();
      el.removeAttribute('src');
      el.load();
      if (sid) void iptv.stop(sid);
    };
  }, [accountId, streamId, slot]);

  useEffect(() => {
    if (video.current) video.current.muted = muted;
  }, [muted]);

  return (
    <button className="mv-video-btn" onClick={onActivate} aria-label="Geluid van deze tegel">
      <video ref={video} className="mv-video" playsInline muted onPlaying={() => setLoading(false)} onWaiting={() => setLoading(true)} />
      {loading && !error && <span className="player-spinner" aria-hidden="true" />}
      {error && <span className="mv-error">{error}</span>}
    </button>
  );
}

function ChannelPicker({ channels, onPick, onClose }: { channels: IptvChannel[]; onPick: (c: IptvChannel) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = s ? channels.filter((c) => c.name.toLowerCase().includes(s)) : channels.filter((c) => c.favorite);
    return (base.length || s ? base : channels).slice(0, 60);
  }, [channels, q]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Zender kiezen" onClick={(e) => e.stopPropagation()}>
        <h2>Zender kiezen</h2>
        <label htmlFor="mvq" className="visually-hidden">Zoek zender</label>
        <input id="mvq" ref={input} className="text-input" type="search" placeholder={channels.some((c) => c.favorite) ? 'Zoek zender (leeg = favorieten)…' : 'Zoek zender…'} value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="mv-pick-list" data-focus-group>
          {list.map((c) => (
            <button key={`${c.accountId}:${c.streamId}`} className="mv-pick" onClick={() => onPick(c)}>
              <ChannelLogo src={c.logo} name={c.name} size={28} />
              <ChannelName name={c.name} />
            </button>
          ))}
          {list.length === 0 && <p className="subtle">Geen zenders gevonden.</p>}
        </div>
        <div className="form-actions"><button className="btn" onClick={onClose}>Annuleren</button></div>
      </div>
    </div>
  );
}
